import React from "react";
import { ArrowRight, MessageCircleHeart } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VoiceButton } from "@/components/VoiceButton";
import { CoachQuiz } from "@/components/landing/CoachQuiz";
import { APP_URL, COACH_CATEGORIES } from "@/data/landing";
import { Reveal, Btn, slugify } from "@/components/landing/primitives";

const TabActiveColors = () => (
  <style>{`
    [data-testid="coaches-tab-anxiety"][data-state="active"]{background:#F0731A;color:#fff;}
    [data-testid="coaches-tab-ptsd"][data-state="active"]{background:#0B6B5E;color:#fff;}
    [data-testid="coaches-tab-postpartum"][data-state="active"]{background:#C42462;color:#fff;}
  `}</style>
);

export const CoachShowcase = () => (
  <>
    <TabActiveColors />
  <section id="coaches" data-testid="coaches-section" className="py-24 lg:py-32 bg-afwn-surface">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm tracking-wider uppercase font-semibold text-afwn-green">Meet Your Assistants</p>
          <h2 className="mt-3 font-heading font-light tracking-tight text-4xl lg:text-5xl">An assistant for every voice you need</h2>
          <p className="mt-4 text-lg text-afwn-slate">Choose the support style that resonates with you. Each specialist brings their own tone and presence — so support always feels personal.</p>
        </div>
      </Reveal>

      <Reveal>
        <div className="mt-12">
          <CoachQuiz />
        </div>
      </Reveal>

      <div className="mt-20 mb-2 flex items-center gap-4 max-w-2xl mx-auto">
        <span className="h-px flex-1 bg-afwn-navy/10" />
        <span className="text-xs tracking-wider uppercase font-semibold text-afwn-slate">Or browse all assistants</span>
        <span className="h-px flex-1 bg-afwn-navy/10" />
      </div>

      <Tabs defaultValue="anxiety" className="mt-10">
        <Reveal>
          <TabsList data-testid="coaches-tabs" className="mx-auto flex w-full max-w-md h-auto bg-white rounded-full p-1.5 border border-afwn-navy/10">
            {COACH_CATEGORIES.map((cat) => (
              <TabsTrigger
                key={cat.value}
                value={cat.value}
                data-testid={`coaches-tab-${cat.value}`}
                className="flex-1 rounded-full py-2.5 text-sm font-medium data-[state=active]:text-white transition-colors"
                style={{ ["--tw-ring-color"]: cat.color }}
              >
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Reveal>

        {COACH_CATEGORIES.map((cat) => (
          <TabsContent key={cat.value} value={cat.value} className="mt-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-14">
              <Reveal>
                <div>
                  <span className="inline-block px-3 py-1 text-xs tracking-wider uppercase rounded-full font-semibold text-white" style={{ background: cat.color }}>{cat.label} Support</span>
                  <p className="mt-5 text-afwn-slate leading-relaxed" data-testid={`assistant-about-${cat.value}`}>{cat.about}</p>
                  <dl className="mt-6 space-y-5">
                    <div>
                      <dt className="text-xs tracking-wider uppercase font-semibold text-afwn-slate">Focus</dt>
                      <dd className="mt-1 text-lg text-afwn-navy">{cat.focus}</dd>
                    </div>
                    <div>
                      <dt className="text-xs tracking-wider uppercase font-semibold text-afwn-slate">Personality</dt>
                      <dd className="mt-1 text-lg text-afwn-navy">{cat.personality}</dd>
                    </div>
                    <div>
                      <dt className="text-xs tracking-wider uppercase font-semibold text-afwn-slate">Voice Style</dt>
                      <dd className="mt-1 text-lg text-afwn-navy">{cat.voiceStyle}</dd>
                    </div>
                  </dl>
                </div>
              </Reveal>
              <Reveal i={1}>
                <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl shadow-afwn-navy/10 border border-afwn-navy/5">
                  <div className="flex items-center gap-2 text-xs tracking-wider uppercase font-semibold" style={{ color: cat.color }}>
                    <MessageCircleHeart size={16} /> A message from your {cat.label} Assistant
                  </div>
                  <div className="mt-4 relative rounded-2xl rounded-tl-sm p-5 text-afwn-navy text-lg leading-relaxed" style={{ background: `${cat.color}12` }}>
                    <span className="absolute -top-2 left-5 w-4 h-4 rotate-45" style={{ background: `${cat.color}12` }} />
                    <span className="relative">&ldquo;{cat.sample}&rdquo;</span>
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
              {cat.coaches.map((c, i) => (
                <Reveal key={c.name} i={i}>
                  <div className="relative h-full">
                  <a
                    href={`${APP_URL}?focus=${cat.value}&coach=${slugify(c.name)}&voice=${slugify(c.voice)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`coach-card-${c.name.toLowerCase()}`}
                    className="group block bg-white rounded-3xl overflow-hidden border border-afwn-navy/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full"
                  >
                    <div className="relative overflow-hidden">
                      <img src={c.img} alt={`Assistant ${c.name}`} className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105" />
                      <span className="absolute top-3 left-3 w-2.5 h-2.5 rounded-full ring-2 ring-white" style={{ background: cat.color }} />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-2xl leading-none" style={{ color: cat.color }}>{c.name}</h3>
                      <p className="mt-2 text-sm text-afwn-slate">{c.voice}</p>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: cat.color }}>Start with {c.name} <ArrowRight size={13} /></span>
                    </div>
                  </a>
                  <VoiceButton slug={slugify(c.name)} name={c.name} color={cat.color} variant="icon" className="absolute top-3 right-3" />
                  </div>
                </Reveal>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Reveal>
        <div className="mt-14 text-center">
          <Btn href={APP_URL} data-testid="coaches-cta-button" variant="primary">Start Free Trial <ArrowRight size={18} /></Btn>
          <p className="mt-3 text-sm text-afwn-slate">Meet all 12 assistants inside the app — free for 7 days.</p>
          <p className="mt-6 text-xs text-afwn-slate/80 max-w-2xl mx-auto leading-relaxed" data-testid="assistants-disclaimer">Anxiety Fix Wellness Network provides educational and wellness information and is not a medical or mental-health treatment provider. The AI Assistants do not diagnose, treat, cure, or prevent any mental-health condition and are not a substitute for therapy, medical care, or services from a qualified healthcare professional. Individual experiences and outcomes vary.</p>
        </div>
      </Reveal>
    </div>
  </section>
  </>
);
